import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import AgencyXLogo from 'images/shared/AgencyXLogo';
import { Link } from 'components';

gsap.registerPlugin(ScrollTrigger);

const IntroductionAndCoreServices = () => {
  const sectionRef = useRef(null);
  const numbersRef = useRef([]);
  const servicesRef = useRef([]);
  const [animatedNumbers, setAnimatedNumbers] = useState({});

  const inNumbers = [
    {
      id: 1,
      number: '1',
      animateFrom: 0,
      animateTo: 1,
      suffix: '',
      value:
        'committed team from all corners of the globe, bringing a diverse and multicultural approach to our work'
    },
    {
      id: 2,
      number: '15',
      animateFrom: 0,
      animateTo: 15,
      suffix: '+',
      value: 'years as a specialist Healthcare and Pharmaceutical agency'
    },
    {
      id: 3,
      number: '10',
      animateFrom: 0,
      animateTo: 10,
      suffix: '',
      value: 'key agency services'
    },
    {
      id: 4,
      number: '15',
      animateFrom: 0,
      animateTo: 15,
      suffix: '',
      value:
        'talented professionals from designers, to developers, to strategists and live stream engineers'
    }
  ];

  const coreServices = [
    {
      id: 1,
      title: 'Brand & Campaign Strategy',
      link: '/services/healthcare-brand-and-campaign-strategy',
      summary:
        "Working with the world's brightest healthcare brands, together we build campaigns that are not only medically and legally compliant, but also creatively compelling.",
      services: [
        'Unique Team Engagement',
        'Online Team Collaboration',
        'Team Building Gamification',
        'Team Created Marketing Strategy'
      ]
    },
    {
      id: 2,
      title: 'Creative & Design',
      link: '/services/creative-and-design',
      summary:
        'From brand identity and packaging to multimedia storytelling and digital interfaces, our creative team crafts compelling, compliant, and beautifully executed experiences across every medium.',
      services: [
        'Original Creative Concepts',
        'Web Design & Development',
        'App Development',
        'Multimedia Creative Design'
      ]
    },
    {
      id: 3,
      title: 'Web & App Development',
      link: '/services/web-development',
      summary:
        'From simple brochure websites, to a complex web application, or a complete digital transformation, our expert development team specialises in building fast, secure, and scalable solutions that perform exceptionally across all devices.',
      services: [
        'Online Audience Engagement',
        'Branded Online Studio',
        'Live/Prerecorded Shows & Events',
        'Post Event Communications'
      ]
    },
    {
      id: 4,
      title: 'Conference & Event Management',
      link: '/services/healthcare-event-management',
      summary:
        'Expert design, planning and production management of all types of in-person, virtual & hybrid events and conferences by our specialist in house Agency X team.',
      services: [
        'Insights & Strategy Development',
        'Campaign Creative & Design',
        'Project Management',
        'Campaign Analytics'
      ]
    }
  ];

  useEffect(() => {
    const section = sectionRef.current;
    const numbers = numbersRef.current;
    const services = servicesRef.current;

    if (!section) return;

    // Animate numbers on scroll
    ScrollTrigger.create({
      trigger: section,
      start: 'top 70%',
      onEnter: () => {
        // Animate number counters
        inNumbers.forEach((item, index) => {
          if (numbers[index]) {
            gsap.fromTo(
              numbers[index],
              {
                scale: 0.8,
                opacity: 0,
                y: 30
              },
              {
                scale: 1,
                opacity: 1,
                y: 0,
                duration: 0.8,
                delay: index * 0.15,
                ease: 'back.out(1.7)',
                onStart: () => {
                  // Animate the actual number counting
                  const numberObj = { value: item.animateFrom };
                  gsap.to(numberObj, {
                    value: item.animateTo,
                    duration: 1.5,
                    delay: index * 0.15,
                    ease: 'power2.out',
                    onUpdate() {
                      const currentValue = Math.round(numberObj.value);
                      setAnimatedNumbers((prev) => ({
                        ...prev,
                        [item.id]: currentValue
                      }));
                    }
                  });
                }
              }
            );
          }
        });

        // Animate service cards
        services.forEach((service, index) => {
          if (service) {
            gsap.fromTo(
              service,
              {
                y: 50,
                opacity: 0,
                scale: 0.95
              },
              {
                y: 0,
                opacity: 1,
                scale: 1,
                duration: 0.6,
                delay: 1 + index * 0.1,
                ease: 'power2.out'
              }
            );
          }
        });
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <>
      <Section ref={sectionRef}>
        <Columns>
          <Column>
            <SectionTitle>
              <StyledAgencyXLogo />
              Intelligent<br />Healthcare Marketing.
            </SectionTitle>
            <ComplianceText>
              We are <strong>IPHA Code Compliant</strong> and{' '}
              <strong>Veeva trained and certified</strong>.
            </ComplianceText>
            <Link to="/about-us" invert>
              Meet the Team
            </Link>
          </Column>

          <StatsGrid>
            {inNumbers.map((item, index) => (
              <StatItem
                key={item.id}
                ref={(el) => {
                  numbersRef.current[index] = el;
                }}>
                <StatNumber>
                  {animatedNumbers[item.id] !== undefined
                    ? `${animatedNumbers[item.id]}${item.suffix}`
                    : `0${item.suffix}`}
                </StatNumber>
                <StatDescription>{item.value}</StatDescription>
              </StatItem>
            ))}
          </StatsGrid>
        </Columns>
        <Services>
          <SectionTitle>Our Core Services</SectionTitle>
          <ServiceList>
            {coreServices.map((service) => (
              <li key={service.id}>
                <Link to={service.link}>
                  <h4>{service.title}</h4>
                  <p className="summary">{service.summary}</p>
                  <span className="read-more">See More</span>
                </Link>
              </li>
            ))}
          </ServiceList>
        </Services>
      </Section>
    </>
  );
};

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 4rem;
  padding: 3rem 1.25rem;
  max-width: 1200px;
  margin: 0 auto;
  @media screen and (min-width: 48rem) {
    padding: 6rem 0;
  }
`;

const Columns = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  @media screen and (min-width: 48rem) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  gap: 2rem;
`;

const SectionTitle = styled.h3`
  font-weight: 900;
  line-height: 1.1;
  font-size: 3.5rem;
  color: #efede4;

  @media screen and (min-width: 48rem) {
    font-size: 4.5rem;
  }
`;

const Highlight = styled.span`
  background: linear-gradient(135deg, var(--ax-gold) 0%, var(--ax-gold-dark) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StatItem = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  color: #efede4;

  &:nth-child(even) > div:first-child {
    color: var(--ax-gold);
  }
  @media screen and (min-width: 48rem) {
    gap: 2rem;
  }
`;

const StatNumber = styled.div`
  font-size: 4rem;
  font-weight: 900;
  line-height: 1;
  margin-bottom: 1rem;

  @media screen and (min-width: 48rem) {
    font-size: 7rem;
  }
`;

const StatDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
  max-width: 400px;

  @media screen and (min-width: 48rem) {
    font-size: 1.25rem;
  }
`;

const ComplianceText = styled.p`
  margin-top: 3rem;
  font-size: 1.125rem;
  font-weight: 400;
  color: #efede4;

  strong {
    color: var(--ax-gold);
    font-weight: 700;
  }
`;

const Services = styled.div`
  margin-top: 4rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  h4 {
    color: #efede4;
    font-size: 1.5rem;
    margin: 1rem 0;
  }
`;

const ServiceList = styled.ul`
  margin: 0 auto;
  position: relative;
  max-width: 100%;
  list-style-type: none;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0;

  li {
    position: relative;
    overflow: hidden;
  }

  a {
    text-decoration: none;
    width: 100%;
    height: 100%;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    color: inherit;
    position: relative;
    overflow: hidden;

    /* Background fill animation */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 100%;
      width: 100%;
      height: 100%;
      background: var(--ax-gold);
      transition: right 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      z-index: 1;
    }

    &:hover::before {
      right: 0;
    }

    /* Text elements positioning */
    h4, .summary, .read-more {
      position: relative;
      z-index: 2;
      transition: all 0.4s ease;
    }

    h4 {
      color: #efede4;
      margin-bottom: 1rem;
    }

    .summary {
      text-transform: initial;
      color: #efede4;
      font-weight: 400;
      opacity: 1;
      transform: translateY(0);
      transition: opacity 0.3s ease 0.1s, transform 0.3s ease 0.1s;
    }

    .read-more {
      position: absolute;
      bottom: 1.25rem;
      right: 1.25rem;
      color: #1d1d1b;
      font-weight: 600;
      font-size: 0.875rem;
      text-transform: uppercase;
      opacity: 0;
      transform: translateY(20px);
      transition: opacity 0.3s ease 0.2s, transform 0.3s ease 0.2s;
    }

    /* Hover states */
    &:hover h4 {
      color: #1d1d1b;
      transition: color 0.3s ease 0.2s;
    }

    &:hover .summary {
      opacity: 0;
      transform: translateY(-10px);
      transition: opacity 0.2s ease, transform 0.2s ease;
    }

    &:hover .read-more {
      opacity: 1;
      transform: translateY(0);
      transition: opacity 0.3s ease 0.3s, transform 0.3s ease 0.3s;
    }
  }

  @media screen and (min-width: 48rem) {
    display: grid;
    grid-gap: 95px 86px;
    grid-template-columns: repeat(2, 250px);
    justify-content: center;
    padding-top: 3.7rem;
  }

  @media screen and (min-width: 1280px) {
    grid-template-columns: repeat(4, minmax(auto, 260px));
    justify-content: space-between;
    grid-gap: 0;
    width: 100%;
  }
`;

const StyledAgencyXLogo = styled(AgencyXLogo)`
  display: block;
  margin-bottom: 1rem;
  width: 200px;

  path {
    fill: white;

    &:nth-child(2) {
      fill: var(--ax-gold);
    }
  }
`;

export default IntroductionAndCoreServices;
